import React, { useState, useMemo, useCallback, useEffect } from "react"

import MuiModal from "../shared/mui_modal"
import {
  FormControl,
  Grid,
  Autocomplete,
  TextField,
  InputLabel,
  Tooltip,
} from "@mui/material"
import { profiles as profilesClient } from "../../ajax_clients"
import IntuneChip from "@/components/groups/shared/intune_chip"

export default function AssignGroupsModal({
  show,
  onClose,
  onSuccess,
  profileId,
}) {
  const [groups, setGroups] = useState([])
  const [selectedGroupIds, setSelectedGroupIds] = useState([])
  const [loading, setLoading] = useState(true)
  const [policy, setPolicy] = useState(null)

  useEffect(() => {
    if (!show) return

    setLoading(true)

    profilesClient.readGroupsForAssignments(profileId).then(response => {
      setGroups(response.data)
      setLoading(false)
      setPolicy(response.policy)
    })
  }, [profileId, show, setLoading, setGroups, setPolicy])

  const groupOptions = useMemo(() => {
    if (loading) return []

    return groups.map(group => ({
      id: group.id,
      label: group.name,
      disabled:
        (group.isSingletonProfileData && group.isSingletonProfileAssigned) ||
        (group.usedForIntune && !policy?.modifyIntune),
      usedForIntune: group.usedForIntune,
    }))
  }, [groups, loading, policy])

  const handleChange = useCallback(
    (_event, newValue) => {
      if (!newValue) {
        setSelectedGroupIds([])
        return
      }

      setSelectedGroupIds(newValue.map(group => group.id))
    },
    [setSelectedGroupIds],
  )

  const handleCommit = useCallback(() => {
    setLoading(true)
    profilesClient
      .createGroupAssignment(profileId, selectedGroupIds)
      .then(() => {
        setSelectedGroupIds([])
        onSuccess()
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
  }, [profileId, selectedGroupIds, setLoading, onSuccess])

  if (!show) return null

  return (
    <MuiModal
      titleText="Assign Groups"
      buttonText="Assign"
      onClose={onClose}
      onCommit={handleCommit}
    >
      <Grid
        item
        xs={12}
        container
        alignItems="center"
        spacing={2}
        sx={{ minHeight: "120px" }}
      >
        <Grid item xs={2}>
          <InputLabel id="group-select-label">Groups</InputLabel>
        </Grid>
        <Grid item xs={9}>
          <FormControl fullWidth>
            <Autocomplete
              multiple
              id="group-select"
              aria-labelledby="group-select-label"
              options={groupOptions}
              getOptionDisabled={option => option.disabled}
              renderOption={(props, option) => (
                <li
                  {...props}
                  {...(option.disabled
                    ? { onClick: event => event.stopPropagation() }
                    : {})}
                >
                  {option.disabled && !option.usedForIntune ? (
                    <Tooltip
                      title="Groups only support a single profile of this type and one has already been assigned."
                      placement="right"
                    >
                      <span style={{ pointerEvents: "auto" }}>
                        {option.label}
                      </span>
                    </Tooltip>
                  ) : (
                    <div>
                      {option.label}
                      {option.usedForIntune && <IntuneChip sx={{ ml: 1 }} />}
                    </div>
                  )}
                </li>
              )}
              getOptionLabel={option => option.label}
              disableCloseOnSelect
              loading={loading}
              onChange={handleChange}
              disabled={loading}
              renderInput={params => (
                <TextField
                  {...params}
                  variant="standard"
                  placeholder="Select groups"
                />
              )}
            />
          </FormControl>
        </Grid>
      </Grid>
    </MuiModal>
  )
}
